import {
  Box,
  Typography,
  Chip,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAppMode } from '@/hooks/useAppMode.js';
import { AppMode } from '@k-ucap/shared';
import { getFirebaseStatus } from '@/infrastructure/firebase/firebaseStatus.js';
import { useAuthStore } from '@/stores/useAuthStore.js';
import { useAppStore } from '@/stores/useAppStore.js';

export const Header = ({ onMenuToggle, desktopOpen }: { onMenuToggle: () => void; desktopOpen?: boolean }) => {
  const mode = useAppMode();
  const firebaseStatus = getFirebaseStatus();
  const presentationMode = useAppStore((s) => s.presentationMode);
  const userProfile = useAuthStore((s) => s.userProfile);
  const globalRole = useAuthStore((s) => s.globalRole);
  const selectedProject = useAuthStore((s) => s.selectedProject);
  const accessibleProjects = useAuthStore((s) => s.accessibleProjects);
  const authStatus = useAuthStore((s) => s.authStatus);
  const signOut = useAuthStore((s) => s.signOut);
  const selectProject = useAuthStore((s) => s.selectProject);

  const modeColor = mode === AppMode.FIREBASE ? 'success' : mode === AppMode.MOCK ? 'warning' : 'error';
  const isReady = authStatus === 'READY';

  const handleProjectChange = (e: SelectChangeEvent<string>) => {
    selectProject(e.target.value);
  };

  const menuTooltip = desktopOpen === true ? '메뉴 접기' : desktopOpen === false ? '메뉴 펼치기' : '메뉴';

  return (
    <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
      <Toolbar>
        <IconButton color="inherit" edge="start" onClick={onMenuToggle} title={menuTooltip}>
          {desktopOpen ? <ChevronLeftIcon /> : <MenuIcon />}
        </IconButton>
        <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
          K-UCAP
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {isReady && accessibleProjects.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select
                value={selectedProject?.id ?? ''}
                onChange={handleProjectChange}
                displayEmpty
                renderValue={() =>
                  selectedProject
                    ? `${selectedProject.name}${selectedProject.isDemo ? ' (Demo)' : ''}`
                    : '프로젝트 선택'
                }
                sx={{ color: 'inherit', '.MuiSvgIcon-root': { color: 'inherit' } }}
              >
                {accessibleProjects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name} {p.isDemo ? '(Demo)' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {!presentationMode && (
            <>
              <Chip label={`Mode: ${mode}`} size="small" color={modeColor} />
              <Chip
                label={firebaseStatus.initialized ? 'Firebase 연결' : 'Firebase 미연결'}
                size="small"
                color={firebaseStatus.initialized ? 'success' : 'default'}
              />
            </>
          )}
          {isReady && userProfile && (
            <>
              <Chip label={userProfile.displayName} size="small" />
              <Chip label={globalRole} size="small" color="info" />
              <Button size="small" color="inherit" startIcon={<LogoutIcon />} onClick={() => signOut()}>
                로그아웃
              </Button>
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};
